import { inject, Injectable, signal } from '@angular/core';
import { RestApiV2Service } from '../http/rest-api-v2.service';
import { UserService } from '../user-management/user.service';
import { ScreeningCycle } from '../../../shared/models/self-takers/self-taker.model';
import { Pacient } from '../../../shared/models/users/pacient.model';
import { take } from 'rxjs';
import { ClinicalLaboratoryUserInfo } from '../../../shared/models/users/user.model';
import { MessageService } from 'primeng/api';
import { SelfTakerResult, TriageResult } from './self-takers.service';

export enum ClinicalLaboratoryTaskType {
  SELF_TAKER = 1,
  TRIAGE = 2,
  COLPOSCOPY = 3,
  HISTOLOGY = 4
}

export interface DashboardKPI {
  title: string;
  value: string | number;
  change: number;
  changeType: 'increase' | 'decrease';
}

export interface TaskCompletionStats {
  selfTakers: {
    total: number;
    completed: number;
  };
  triages: {
    total: number;
    completed: number;
  };
  colposcopies: {
    total: number;
    completed: number;
  };
  histologies: {
    total: number;
    completed: number;
  };
}

export interface TriageExamsData {
  colposcopies: number;
  genotyping: number;
  via: number;
}

export interface ExamResultsData {
  selfTakers: {
    positive: number;
    negative: number;
  };
  triages: {
    positive: number;
    negative: number;
  };
}

export interface TaskTrendData {
  date: string;
  assigned: number;
  completed: number;
}

export interface ClinicalLaboratoryDashboardStats {
  kpis: DashboardKPI[];
  taskCompletion: TaskCompletionStats;
  triageExams: TriageExamsData;
  examResults: ExamResultsData;
  taskTrend: TaskTrendData[];
}

@Injectable({
  providedIn: 'root',
})
export class ClinicalLaboratoryService {
  private apiService = inject(RestApiV2Service);
  private userService = inject(UserService);
  private messageService = inject(MessageService);

  readonly data = signal
  <{ 
    pacients: {pacient: Pacient, screening_cycles: ScreeningCycle[]}[]
  }>({ pacients: [] });

  getMyData() {
    const user = this.userService.userData()?.user_info as ClinicalLaboratoryUserInfo;
    const response = this.apiService.get<{ data: { pacients: any[] } }>('clinical-laboratory/get-my-data', { laboratory_id: user!.id });

    response.request$
    .pipe(take(1))
    .subscribe(response => {
      console.log(response.data);
      this.data.set(response.data);
    });
    
    response.error$
    .subscribe(error => {
      if(!error) return;

      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al obtener los datos' });
    });
  }

  processData(data?: {pacient: Pacient, screening_cycles: ScreeningCycle[]}[]): ClinicalLaboratoryDashboardStats {
    // Aplanar el array de arrays en un único array de ScreeningCycle
    const allScreeningCycles = data?.flatMap(item => item.screening_cycles) || [];

    const stats: ClinicalLaboratoryDashboardStats = {
      kpis: this.calculateKPIs(allScreeningCycles),
      taskCompletion: this.calculateTaskCompletion(allScreeningCycles),
      triageExams: this.calculateTriageExams(allScreeningCycles),
      examResults: this.calculateExamResults(allScreeningCycles),
      taskTrend: this.calculateTaskTrend(allScreeningCycles),
    };

    return stats;
  }

  private calculateKPIs(screeningCycles: ScreeningCycle[]): DashboardKPI[] {
    const selfTakersReceived = screeningCycles.filter(cycle => {
      const selfTakerTask = cycle.clinical_laboratory_tasks.find(task => task.task_id === ClinicalLaboratoryTaskType.SELF_TAKER);
      return selfTakerTask;
    }).length;

    const triagesPending = screeningCycles.filter(cycle => {
      const triageTask = cycle.clinical_laboratory_tasks.find(task => task.task_id === ClinicalLaboratoryTaskType.TRIAGE);
      return triageTask;
    }).length;

    const colposcopiesPending = screeningCycles.filter(cycle => {
      const colposcopyTask = cycle.clinical_laboratory_tasks.find(task => task.task_id === ClinicalLaboratoryTaskType.COLPOSCOPY);
      return colposcopyTask;
    }).length;

    const histologiesPending = screeningCycles.filter(cycle => {
      const histologyTask = cycle.clinical_laboratory_tasks.find(task => task.task_id === ClinicalLaboratoryTaskType.HISTOLOGY);
      return histologyTask;
    }).length;

    return [
      {
        title: 'Auto tomas recibidas',
        value: selfTakersReceived.toLocaleString(),
        change: 10, // TODO: Calculate real change vs previous month
        changeType: 'increase'
      },
      {
        title: 'Triages pendientes',
        value: triagesPending.toLocaleString(),
        change: 5, // TODO: Calculate real change vs previous month
        changeType: 'increase'
      },
      {
        title: 'Colposcopias pendientes',
        value: colposcopiesPending.toLocaleString(),
        change: 8, // TODO: Calculate real change vs previous month
        changeType: 'increase'
      },
      {
        title: 'Patologías pendientes',
        value: histologiesPending.toLocaleString(),
        change: 3, // TODO: Calculate real change vs previous month
        changeType: 'increase'
      }
    ];
  }

  private calculateTaskCompletion(screeningCycles: ScreeningCycle[]): TaskCompletionStats {
    // Self takers: total and completed
    const selfTakersTasks = screeningCycles
      .map(cycle => cycle.clinical_laboratory_tasks.find(task => task.task_id === ClinicalLaboratoryTaskType.SELF_TAKER))
      .filter(task => task !== undefined);
    
    const selfTakersTotal = selfTakersTasks.length;
    const selfTakersCompleted = selfTakersTasks.filter(task => task.task_finished === true).length;

    // Triages: total and completed
    const triagesTasks = screeningCycles
      .map(cycle => cycle.clinical_laboratory_tasks.find(task => task.task_id === ClinicalLaboratoryTaskType.TRIAGE))
      .filter(task => task !== undefined);
    
    const triagesTotal = triagesTasks.length;
    const triagesCompleted = triagesTasks.filter(task => task.task_finished === true).length;

    // Colposcopies: total and completed
    const colposcopiesTasks = screeningCycles
      .map(cycle => cycle.clinical_laboratory_tasks.find(task => task.task_id === ClinicalLaboratoryTaskType.COLPOSCOPY))
      .filter(task => task !== undefined);
    
    const colposcopiesTotal = colposcopiesTasks.length;
    const colposcopiesCompleted = colposcopiesTasks.filter(task => task.task_finished === true).length;

    // Histologies: total and completed
    const histologiesTasks = screeningCycles
      .map(cycle => cycle.clinical_laboratory_tasks.find(task => task.task_id === ClinicalLaboratoryTaskType.HISTOLOGY))
      .filter(task => task !== undefined);
    
    const histologiesTotal = histologiesTasks.length;
    const histologiesCompleted = histologiesTasks.filter(task => task.task_finished === true).length;

    return {
      selfTakers: {
        total: selfTakersTotal,
        completed: selfTakersCompleted,
      },
      triages: {
        total: triagesTotal,
        completed: triagesCompleted,
      },
      colposcopies: {
        total: colposcopiesTotal,
        completed: colposcopiesCompleted,
      },
      histologies: {
        total: histologiesTotal,
        completed: histologiesCompleted,
      },
    };
  }

  private calculateTriageExams(screeningCycles: ScreeningCycle[]): TriageExamsData {
    // Colposcopies: cycles that have self_taker_triage with colposcopy_completed === true
    const colposcopies = screeningCycles.filter(cycle => 
      cycle.self_taker_triage?.colposcopy_completed === true
    ).length;

    // VIA: cycles that have self_taker_triage with via_completed === true
    const via = screeningCycles.filter(cycle => 
      cycle.self_taker_triage?.via_completed === true
    ).length;

    // Genotyping: cycles that have self_taker_triage with genotyping_completed === true
    // or cycles that have self_taker_histology
    const genotyping = screeningCycles.filter(cycle => 
      (cycle.self_taker_triage?.genotyping_completed === true) ||
      (cycle.self_taker_histology !== undefined)
    ).length;

    return {
      colposcopies,
      genotyping,
      via
    };
  }

  private calculateExamResults(screeningCycles: ScreeningCycle[]): ExamResultsData {
    // Self takers: positive and negative based on self_taker.result_id
    const selfTakersPositive = screeningCycles.filter(cycle => 
      cycle.self_taker?.result_id === SelfTakerResult.POSITIVE
    ).length;

    const selfTakersNegative = screeningCycles.filter(cycle => 
      cycle.self_taker?.result_id === SelfTakerResult.NEGATIVE
    ).length;

    // Triages: positive and negative based on self_taker_triage.result_id
    const triagesPositive = screeningCycles.filter(cycle => 
      cycle.self_taker_triage?.result_id === TriageResult.POSITIVE
    ).length;

    const triagesNegative = screeningCycles.filter(cycle => 
      cycle.self_taker_triage?.result_id === TriageResult.NEGATIVE
    ).length;

    return {
      selfTakers: {
        positive: selfTakersPositive,
        negative: selfTakersNegative,
      },
      triages: {
        positive: triagesPositive,
        negative: triagesNegative,
      },
    };
  }

  private calculateTaskTrend(screeningCycles: ScreeningCycle[]): TaskTrendData[] {
    // Group all tasks by month based on created_at
    const tasksByMonth = new Map<string, { assigned: number; completed: number }>();

    screeningCycles.forEach(cycle => {
      if (cycle.clinical_laboratory_tasks && cycle.clinical_laboratory_tasks.length > 0) {
        cycle.clinical_laboratory_tasks.forEach(task => {
          const date = new Date(task.created_at);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          const monthLabel = date.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' });

          if (!tasksByMonth.has(monthKey)) {
            tasksByMonth.set(monthKey, { assigned: 0, completed: 0 });
          }

          const monthData = tasksByMonth.get(monthKey)!;
          monthData.assigned += 1;
          
          if (task.task_finished) {
            monthData.completed += 1;
          }
        });
      }
    });

    // Convert to array and sort by date
    const sortedMonths = Array.from(tasksByMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, data]) => {
        const [year, month] = key.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        const monthLabel = date.toLocaleDateString('es-ES', { month: 'short' });
        
        return {
          date: monthLabel,
          assigned: data.assigned,
          completed: data.completed,
        };
      });

    // If no data, return empty array
    if (sortedMonths.length === 0) {
      return [];
    }

    return sortedMonths;
  }

  manageClinicalLaboratoryData(cycle: ScreeningCycle) {
    this.data.update(currentData => {
      // Get current clinical laboratory ID
      const user = this.userService.userData();
      const currentLaboratoryId = user?.id;

      if (!currentLaboratoryId) {
        return currentData;
      }

      // Filter tasks that belong to current clinical laboratory
      const relevantTasks = (cycle.clinical_laboratory_tasks || []).filter(
        task => task.clinical_laboratory_id === currentLaboratoryId
      );

      // Helper function to update or add cycle in a patients array
      const updateOrAddCycle = (pacientsArray: {pacient: Pacient, screening_cycles: ScreeningCycle[]}[]) => {
        return pacientsArray.map(item => {
          // Find if the patient has this cycle
          const cycleIndex = item.screening_cycles.findIndex(c => c.id === cycle.id);
          
          if (cycleIndex !== -1) {
            // Update existing cycle, preserving tasks from other laboratories
            const existingCycle = item.screening_cycles[cycleIndex];
            const existingTasks = existingCycle.clinical_laboratory_tasks || [];
            
            // Keep tasks from other laboratories
            const otherLaboratoryTasks = existingTasks.filter(
              task => task.clinical_laboratory_id !== currentLaboratoryId
            );
            
            // Merge relevant tasks: update existing or add new ones
            const mergedRelevantTasks = [...otherLaboratoryTasks];
            relevantTasks.forEach(newTask => {
              const existingTaskIndex = mergedRelevantTasks.findIndex(t => t.id === newTask.id);
              if (existingTaskIndex !== -1) {
                mergedRelevantTasks[existingTaskIndex] = newTask;
              } else {
                mergedRelevantTasks.push(newTask);
              }
            });
            
            const updatedCycles = [...item.screening_cycles];
            updatedCycles[cycleIndex] = {
              ...cycle,
              clinical_laboratory_tasks: mergedRelevantTasks
            };
            return { ...item, screening_cycles: updatedCycles };
          } else if (String(item.pacient.id) === String(cycle.pacient_id)) {
            // The cycle belongs to this patient but doesn't exist, add it with filtered tasks
            return { 
              ...item, 
              screening_cycles: [
                ...item.screening_cycles, 
                { ...cycle, clinical_laboratory_tasks: relevantTasks }
              ] 
            };
          }
          return item;
        });
      };

      // Actualizar pacients
      const updatedPacients = updateOrAddCycle(currentData.pacients);
      
      // Verificar si el paciente existe, si no, agregarlo
      const pacientExists = updatedPacients.some(item => String(item.pacient.id) === String(cycle.pacient_id));
      if (!pacientExists && cycle.pacient) {
        updatedPacients.push({
          pacient: cycle.pacient,
          screening_cycles: [cycle]
        });
      }

      return {
        pacients: updatedPacients
      };
    });
  }
}
